import { z } from 'zod';
/** These enums mirror the database enums; keep both in sync. */
/** `SUPPORT` reads and answers support mail and sees nothing else of the admin area. */
export const userRoleSchema = z.enum(['USER', 'ADMIN', 'SUPPORT']);
/** Roles that may open the support inbox. */
export const SUPPORT_STAFF_ROLES = ['ADMIN', 'SUPPORT'];
export const userStatusSchema = z.enum(['ACTIVE', 'DEACTIVATED', 'PENDING_DELETION']);
export const measurementSystemSchema = z.enum(['METRIC', 'US', 'UK']);
export const recipeStatusSchema = z.enum(['DRAFT', 'READY', 'ARCHIVED']);
export const recipeVisibilitySchema = z.enum(['PRIVATE', 'HOUSEHOLD', 'UNLISTED', 'PUBLIC']);
/** Who authored the content: the user, an imported source or an AI generation. */
export const recipeOriginSchema = z.enum(['USER', 'IMPORTED', 'AI_GENERATED']);
export const difficultySchema = z.enum(['EASY', 'MEDIUM', 'HARD']);
export const sectionTypeSchema = z.enum(['INGREDIENTS', 'STEPS']);
export const mediaRoleSchema = z.enum([
    'COVER',
    'GALLERY',
    'STEP',
    'COOKING_LOG',
    'AI_INPUT',
    'AVATAR',
]);
export const mediaStatusSchema = z.enum([
    'PENDING',
    'UPLOADED',
    'PROCESSING',
    'READY',
    'FAILED',
    'DELETED',
]);
export const mediaPurposeSchema = z.enum([
    'RECIPE',
    'AVATAR',
    'AI_INPUT',
    'COOKING_LOG',
    'EXPORT',
    'TASTING_NOTE',
    'PRODUCT',
    'RECEIPT',
    'THEME_BACKGROUND',
    'LOYALTY_CARD',
]);
export const unitDimensionSchema = z.enum([
    'MASS',
    'VOLUME',
    'COUNT',
    'LENGTH',
    'TEMPERATURE',
    'OTHER',
]);
export const unitSystemSchema = z.enum(['METRIC', 'US', 'UNIVERSAL']);
export const householdRoleSchema = z.enum(['OWNER', 'EDITOR', 'VIEWER']);
export const invitationStatusSchema = z.enum([
    'PENDING',
    'ACCEPTED',
    'DECLINED',
    'EXPIRED',
    'REVOKED',
]);
export const sourceTypeSchema = z.enum(['URL', 'MANUAL', 'AI', 'IMAGE', 'BOOK', 'OTHER']);
export const importStatusSchema = z.enum([
    'QUEUED',
    'FETCHING',
    'PARSING',
    'REVIEW',
    'CONFIRMED',
    'FAILED',
    'CANCELLED',
    'EXPIRED',
]);
export const importStrategySchema = z.enum([
    'JSON_LD',
    'MICRODATA',
    'SITE_ADAPTER',
    'GENERIC_HTML',
    'AI_FALLBACK',
]);
export const aiCapabilitySchema = z.enum([
    'RECIPE_FROM_TEXT',
    'RECIPE_FROM_IMAGES',
    'CONVERSATION',
    'SOURCE_SEARCH',
    'TAG_SUGGESTION',
    'COOKING_QUESTION',
    'OCR',
    'MEAL_SUGGESTION',
    'IMPORT_STRUCTURING',
    'TRANSCRIPTION',
    'RECIPE_FROM_TASTING_NOTE',
    'PRODUCT_LABEL_EXTRACTION',
    'PRICE_TAG_EXTRACTION',
    'RECEIPT_EXTRACTION',
    'SHOPPING_UTTERANCE_PARSE',
    'RECIPE_FROM_VIDEO',
]);
export const aiProviderSchema = z.enum(['openai', 'anthropic', 'gemini', 'mock']);
export const aiGenerationStatusSchema = z.enum([
    'QUEUED',
    'RUNNING',
    'SUCCEEDED',
    'FAILED',
    'CANCELLED',
]);
export const aiFeedbackSchema = z.enum(['UP', 'DOWN']);
export const labelKindSchema = z.enum(['DIETARY', 'ALLERGEN']);
export const labelSourceSchema = z.enum(['USER', 'AI', 'IMPORT']);
export const timerStatusSchema = z.enum(['RUNNING', 'PAUSED', 'DONE', 'CANCELLED']);
export const mealSlotSchema = z.enum(['BREAKFAST', 'LUNCH', 'DINNER', 'SNACK', 'CUSTOM']);
export const shoppingListStatusSchema = z.enum(['ACTIVE', 'ARCHIVED']);
export const notificationTypeSchema = z.enum([
    'IMPORT_READY',
    'IMPORT_FAILED',
    'AI_READY',
    'AI_FAILED',
    'TIMER_DONE',
    'HOUSEHOLD_INVITE',
    'HOUSEHOLD_CHANGE',
    'EXPORT_READY',
    'SYSTEM',
    'SUPPORT_MESSAGE',
    'SHOPPING_REMINDER',
]);
export const jobStatusSchema = z.enum([
    'QUEUED',
    'ACTIVE',
    'COMPLETED',
    'FAILED',
    'DELAYED',
    'WAITING',
    'CANCELLED',
]);
export const abuseReportStatusSchema = z.enum(['OPEN', 'REVIEWED', 'DISMISSED', 'ACTIONED']);
export const domainRuleModeSchema = z.enum(['ALLOW', 'BLOCK']);
export const DIETARY_LABELS = [
    'vegetarian',
    'vegan',
    'pescatarian',
    'gluten_free',
    'dairy_free',
    'lactose_free',
    'nut_free',
    'egg_free',
    'soy_free',
    'low_carb',
    'keto',
    'paleo',
    'halal',
    'kosher',
    'sugar_free',
    'low_sodium',
];
export const dietaryLabelSchema = z.enum(DIETARY_LABELS);
/** EU "14 allergens" plus common extras. */
export const ALLERGENS = [
    'gluten',
    'crustaceans',
    'eggs',
    'fish',
    'peanuts',
    'soy',
    'milk',
    'tree_nuts',
    'celery',
    'mustard',
    'sesame',
    'sulphites',
    'lupin',
    'molluscs',
];
export const allergenSchema = z.enum(ALLERGENS);
export const MEAL_TYPES = [
    'breakfast',
    'brunch',
    'lunch',
    'dinner',
    'snack',
    'dessert',
    'appetizer',
    'side',
    'drink',
    'sauce',
    'baking',
];
export const mealTypeSchema = z.enum(MEAL_TYPES);
/**
 * The departments of a store, in the order of a usual walk through it (the default order of a
 * shopping list; a store can have its own, see `Store.departmentOrder`). `pantry` is what is left
 * of the dry goods after the finer departments. Additive: clients show an unknown code as `other`.
 */
export const GROCERY_CATEGORIES = [
    'produce',
    'bakery',
    'meat_fish',
    'dairy_eggs',
    'frozen',
    'baking_goods',
    'pasta_rice',
    'canned',
    'oils_sauces',
    'spices',
    'breakfast',
    'snacks',
    'pantry',
    'beverages',
    'alcohol',
    'drugstore',
    'household',
    'pet',
    'other',
];
export const groceryCategorySchema = z.enum(GROCERY_CATEGORIES);
//# sourceMappingURL=enums.js.map